import React, { FC, useState, ReactElement, } from "react";

import { SquareMinusIcon, SquarePlusIcon, ChevronDownIcon, ChevronUpIcon, } from "./Icons";
import { Button, ButtonStyle, classNames, parseClasses, } from ".";

type Props = {
    label: string;
    svg?: JSX.Element;
    headingLevel?: number;
    isCollapsed?: boolean;
    className?: string | string[];
    contentClassName?: string | string[];

    onToggle?: (isCollapsed: boolean) => void;

    children?: React.ReactNode;
};

export const Collapsible: FC<Props> = (props: Props): ReactElement => {
    const { label, headingLevel, isCollapsed, className, contentClassName, children, onToggle, svg, } = props;

    const [collapsed, setCollapsed] = useState(!!isCollapsed);

    const handleToggle = () => {
        const c = !collapsed;
        setCollapsed(c);
        onToggle && onToggle(c);
    };

    const button = renderToggleButton(label, collapsed, handleToggle, svg);
    const heading = renderHeading(button, headingLevel);

    const sectionCss = classNames([
        parseClasses(className),
        "__collapsible__",
        isCollapsed ? "collapsed" : "",
    ]);

    const contentCss = classNames([
        parseClasses(contentClassName),
        "content",
    ]);

    return (
        <section className={sectionCss}>
            <header>{heading}</header>

            <div className={contentCss}>
                {/* always render the children to preserve state; hide it via CSS */}
                {children}
            </div>
        </section>
    );
};

const renderToggleButton = (
    label: string,
    isCollapsed: boolean,
    handleToggle: () => void,
    svg?: JSX.Element,
): ReactElement => {
    const icon = svg || (isCollapsed ? SquarePlusIcon : SquareMinusIcon);
    const chevron = svg ? (isCollapsed ? ChevronDownIcon : ChevronUpIcon) : <></>;
    const css = classNames([
        "w-100",
        "text-start",
        "__togglebutton__",
    ]);
    return (
        <Button
            className={css}
            style={ButtonStyle.Link}
            onClick={handleToggle}
        >
            <span className="__icon__">{icon}</span>
            <span className="__label__">{label}</span>
            <span className="__chevron__">{chevron}</span>
        </Button>
    );
};

const renderHeading = (button: ReactElement, headingLevel?: number): ReactElement => {
    // there must be a better way to do this
    switch (headingLevel) {
        case 0:
            return <span>{button}</span>;

        case 1:
            return <h1>{button}</h1>;

        case 3:
            return <h3>{button}</h3>;

        case 4:
            return <h4>{button}</h4>;

        case 5:
            return <h5>{button}</h5>;

        case 6:
            return <h6>{button}</h6>;

        // h2 is the default
        default:
            return <h2>{button}</h2>;
    }
};
